import { type JSX, Show, splitProps } from 'solid-js'
import { cn } from '../lib/cn.ts'
import { tv, type VariantProps } from '../lib/tv.ts'

/**
 * The standard container. Before this existed every screen hand-wrote
 * `rounded-card border border-border bg-surface p-6`, which is how a design system quietly stops
 * being one.
 *
 * Two rules it enforces:
 *
 *   · a Panel never nests inside a Panel. Boxes inside boxes are the fastest way to make a dense
 *     console look cheap. Use `divide-y` or a heading inside one panel instead.
 *   · `padded={false}` when the body is a Table, so the table's own cell padding is the only
 *     padding and the header row reaches the panel edge.
 */
const panel = tv({
  slots: {
    root: 'rounded-panel border border-border bg-surface',
    header: 'flex items-center justify-between gap-4 border-b border-border px-4 py-3',
    heading: 'grid gap-0.5',
    title: 'text-ui font-semibold tracking-tight text-text',
    description: 'text-body text-text-subtle',
    actions: 'flex shrink-0 items-center gap-2',
    body: '',
  },
  variants: {
    padded: {
      true: { body: 'p-4' },
      false: { body: '' },
    },
  },
  defaultVariants: { padded: true },
})

export interface PanelProps
  extends Omit<JSX.HTMLAttributes<HTMLElement>, 'title'>,
    VariantProps<typeof panel> {
  title?: string
  description?: string
  /** Rendered at the right of the header. Keep to one or two controls. */
  actions?: JSX.Element
  children: JSX.Element
}

export const Panel = (props: PanelProps) => {
  const [local, rest] = splitProps(props, [
    'title',
    'description',
    'actions',
    'padded',
    'children',
    'class',
  ])
  const styles = () => panel({ padded: local.padded })

  return (
    <section {...rest} class={cn(styles().root(), local.class)}>
      <Show when={local.title}>
        {(title) => (
          <header class={styles().header()}>
            <div class={styles().heading()}>
              <h2 class={styles().title()}>{title()}</h2>
              <Show when={local.description}>
                {(description) => <p class={styles().description()}>{description()}</p>}
              </Show>
            </div>
            <Show when={local.actions}>
              {(actions) => <div class={styles().actions()}>{actions()}</div>}
            </Show>
          </header>
        )}
      </Show>
      <div class={styles().body()}>{local.children}</div>
    </section>
  )
}

/**
 * A single headline figure. Deliberately not a card: at four across, four bordered boxes read as
 * four separate widgets, while four columns split by a hairline read as one instrument.
 */
export interface MetricProps extends JSX.HTMLAttributes<HTMLDivElement> {
  label: string
  value: string
  /** Short qualifier — a delta, a unit, a period. */
  note?: string
  tone?: 'normal' | 'done' | 'warning' | 'critical'
}

const metricTone = {
  normal: 'text-text',
  done: 'text-status-done',
  warning: 'text-status-warning',
  critical: 'text-status-critical',
} as const

export const Metric = (props: MetricProps) => {
  const [local, rest] = splitProps(props, ['label', 'value', 'note', 'tone', 'class'])

  return (
    <div {...rest} class={cn('grid gap-1 px-4 py-3.5', local.class)}>
      <span class="text-micro font-semibold text-text-subtle uppercase">{local.label}</span>
      <span
        class={cn(
          'font-mono text-display leading-none font-medium tabular-nums',
          metricTone[local.tone ?? 'normal'],
        )}
      >
        {local.value}
      </span>
      <Show when={local.note}>
        {(note) => <span class="text-body text-text-subtle">{note()}</span>}
      </Show>
    </div>
  )
}
